// src/components/ui/SalidaAlmacenForm.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";

import InputField from "./InputField";
import SelectField from "./SelectField";
import TextAreaField from "./TextAreaField";

const SalidaAlmacenForm = ({ onClose, onSave }) => {
  const [form, setForm] = useState({
    fecha_salida: new Date().toISOString().split("T")[0],
    usuarioId: "",
    observaciones: "",
  });

  const [almacenes, setAlmacenes] = useState([]);
  const [almacenId, setAlmacenId] = useState("");

  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [cantidad, setCantidad] = useState(0);

  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  /* =========================
     CARGAR USUARIO
  ========================== */
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user?.id) {
      setForm((s) => ({ ...s, usuarioId: user.id }));
    }
  }, []);

  /* =========================
     CARGAR ALMACENES
  ========================== */
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const { data } = await axios.get(`${API}/almacenes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAlmacenes(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error("No se pudieron cargar los almacenes");
      }
    };

    fetchWarehouses();
  }, [API, token]);

  /* =========================
     CARGAR STOCK POR ALMACÉN
  ========================== */
  const fetchStockByWarehouse = async (id) => {
    try {
      const { data } = await axios.get(`${API}/almacenes/${id}/stock`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(data.stock) ? data.stock : []);
    } catch (err) {
      toast.error("No se pudo cargar el stock del almacén");
      setItems([]);
    }
  };

  const handleWarehouseChange = (e) => {
    const id = e.target.value;
    setAlmacenId(id);
    setSelectedItem("");
    setCantidad(0);
    setItems([]);

    if (id) fetchStockByWarehouse(id);
  };

  /* =========================
     ITEM SELECCIONADO
  ========================== */
  const selected = items.find((i) => i.codigo_item === selectedItem);

  /* =========================
     SUBMIT
  ========================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!almacenId) {
      toast.error("Debe seleccionar un almacén");
      return;
    }

    if (!selected) {
      toast.error("Debe seleccionar un ítem válido");
      return;
    }

    if (cantidad <= 0) {
      toast.warning("La cantidad debe ser mayor a cero");
      return;
    }

    if (cantidad > selected.stock_actual) {
      toast.error("No puede retirar más de lo disponible");
      return;
    }

    const payload = {
      usuarioId: form.usuarioId,
      observaciones: form.observaciones,
      tipo_movimiento: "Salida",
      almacenId,
      items: [
        {
          codigo_item: selected.codigo_item,
          articulo: selected.articulo,
          cantidad,
          precio_unitario: selected.precio_unitario ?? 0,
        },
      ],
    };

    setLoading(true);
    try {
      const res = await axios.post(`${API}/stock/stock-multiple`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(res.data.message || "Salida registrada");
      onSave?.(payload);
      onClose();
    } catch (err) {
      toast.error("Error al registrar la salida");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     UI
  ========================== */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">📤 Salida de almacén</h2>
              <button onClick={onClose}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <SelectField
                label="🏢 Almacén *"
                value={almacenId}
                options={almacenes}
                valueKey="_id"
                displayFn={(a) => a.nombre}
                placeholder="Seleccione un almacén"
                onChange={(e) => handleWarehouseChange(e)}
              />

              {almacenId && (
                <SelectField
                  label="📦 Ítem disponible *"
                  value={selectedItem}
                  options={items}
                  valueKey="codigo_item"
                  displayFn={(i) => `${i.articulo} (${i.stock_actual})`}
                  placeholder="Seleccione un ítem"
                  onChange={(e) => setSelectedItem(e.target.value)}
                />
              )}

              {selected && (
                <div className="grid grid-cols-3 gap-3 bg-gray-50 p-3 rounded text-sm">
                  <div>
                    <strong>Stock:</strong> {selected.stock_actual}
                  </div>
                  <div>
                    <strong>Precio:</strong> ${selected.precio_unitario ?? 0}
                  </div>
                  <div>
                    <strong>Final:</strong>{" "}
                    {Math.max(selected.stock_actual - cantidad, 0)}
                  </div>
                </div>
              )}

              <InputField
                label="🔢 Cantidad *"
                type="number"
                min={1}
                value={cantidad}
                onChange={(e) => setCantidad(Number(e.target.value))}
              />

              <TextAreaField
                label="📝 Observaciones"
                value={form.observaciones}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    observaciones: e.target.value,
                  }))
                }
              />

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="bg-red-500 text-white"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white"
                >
                  {loading ? "Procesando..." : "Registrar salida"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalidaAlmacenForm;
